import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';
import ElementsTable from '../UI/elementsTable';

import {
  getGamesSessions,
  decisionsInsigtsQuery,
  itemsQuery,
  gamesQuery
} from './api';

import { 
  popupMessagesSummaryDecisionBreakdown,
  summaryDecisionBreakdownElementsHeaders,
  summaryDecisionBreakdownAllOtherAccountsHeaders
} from './data';

import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';

export default function ClassificationSummaryDecisionBreakdown() {
  const navigate = useNavigate();
  
  const { 
    getUser,
    getCurrentClassificationGameId
  } = useAuth();

  const user = getUser();
  
  const gameId = getCurrentClassificationGameId();

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);
  const [isFirstGameOfTheType, setIsFirstGameOfTheType] = useState(0);

  const [elementsTableData, setElementsTableData] = useState([]);
  const [allOtherAccountsTableData, setAllOtherAccountsTableData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const { data: gamesSessions } = useQuery({
    queryKey: ['gamesSessionsSummaryBreakdown'],
    queryFn: getGamesSessions,
    refetchOnWindowFocus: false,
  });

  const { data: games } = useQuery(gamesQuery()) || {};
  const { data: decisionsInsights } = useQuery(decisionsInsigtsQuery({gameId, gameSessionId: currentGameSessionId}));
  const { data: items } = useQuery(itemsQuery());

  useEffect(() => {
    if(gamesSessions && games && gameId && user) {
      if(gamesSessions.filter(
          session =>
            games.find(game => game.id === session.game && game.game_type === 'Account Classification')
        ).length < 2) {
        setIsFirstGameOfTheType(true);
      }

      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionId(completedGameSession?.id);
      }
    }
  }, [gamesSessions, games, gameId, user]);

  useEffect(() => {
    if(decisionsInsights && items) {
      setDataLoaded(true);
      console.log('decisionsInsights', decisionsInsights);
      const elementsData = [];
      const allOtherAccountsData = [];
      
      decisionsInsights.map(decisionInsight => {
        const row = {
          question: decisionInsight.question,
        };

        decisionInsight.options.forEach(insightsOption => {
          const optionItem = items.find(item => item.name === insightsOption.option);
          const questionItem = items.find(item => item.name === row.question);
          const isParentMatch = questionItem && optionItem && questionItem.parents.includes(optionItem.id);
  
          row[insightsOption.option] = {
            value: insightsOption.count_users,
            isParentMatch,
            background: insightsOption.count_users 
              ? isParentMatch 
                ? '#D1E9D6' 
                : '#FD9595'
              : '#FFFFFF',
          };
        });
        
        if (summaryDecisionBreakdownAllOtherAccountsHeaders.includes(row.question)) {
          elementsData.push(row);
        } else {
          allOtherAccountsData.push(row);
        }
        
        return row;
      });

      console.log('elementsData', elementsData);
      setElementsTableData(elementsData);
      setAllOtherAccountsTableData(allOtherAccountsData);

    }
  }, [decisionsInsights, items]);
   
  const handlePopupMessagesNextButtonClick = () => {
    let redirectPath = '/classification/summary/final';
    if(isFirstGameOfTheType) {
      redirectPath = '/classification/summary/feedback';
    }
    navigate(redirectPath);
  }
  
  const handlePopupMessagesPrevButtonClick = () => {
    navigate('/classification/summary/breakdown');
  }
  
  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        dataLoaded &&
          <div className="game-summary-container game-summary-decision-breakdown-container">
            <PopupMessages
              messages={popupMessagesSummaryDecisionBreakdown}
              messageSize="small"
              buttonText="Next"
              onButtonClick={handlePopupMessagesNextButtonClick}
              additionalButtonText="Back"
              onAdditionalButtonClick={handlePopupMessagesPrevButtonClick}
            />
            <div className="summary-decision-breakdown-tables">
              <div className="summary-decision-breakdown-table-container">
                <ElementsTable 
                  title="The Elements"
                  tableColumnHeader="Sorting Items"
                  tableHeaders={summaryDecisionBreakdownElementsHeaders}
                  tableData={elementsTableData}
                />
              </div>
              
              {
                allOtherAccountsTableData.length > 0 &&
                  <div className="summary-decision-breakdown-table-container">
                    <ElementsTable 
                      title="All other Accounts (incl. the Tricky Accounts)"
                      tableColumnHeader="Sorting Items"
                      tableHeaders={summaryDecisionBreakdownAllOtherAccountsHeaders}
                      tableData={allOtherAccountsTableData}
                    />
                  </div>
              }
            </div>
          </div>
      }
    </Layout>
  );
}
